// Deterministic owner-name → color helper.
//
// Used by the parcel-detail mini-map adjacent-parcel overlay, the
// main SearchPage map "Color by owner" toggle, and the NeighborsCard /
// OwnerPortfolioCard pills so the cards and the map visually agree:
// same owner == same color, always.
//
// Approach:
//   1. Normalize the owner string the same way the backend does for
//      portfolio matching (trim + collapse whitespace + uppercase) so
//      "DOE, JOHN  TRUST" and " doe, john trust " hash identically.
//   2. Hash to an HSL hue 0–360 (saturation 65%, lightness 50%).
//   3. Reserved buckets get explicit colors so they look stable across
//      every page: null owner = neutral gray, government = federal-style
//      muted brown, "individual owner" placeholder = soft blue.
//
// The hash is the FNV-1a 32-bit variant — small, branchless, no deps,
// and gives a well-spread hue distribution for short strings (the
// average owner name is < 30 chars).

const RESERVED = {
  // No owner data on the parcel at all. Distinct from "government" so
  // a buyer can tell the difference at a glance on the map.
  NULL_OWNER: '#9ca3af',      // tailwind gray-400
  // Federal / state / local government holdings. Earthy brown matches
  // the existing federal-lands styling on MapView (AGENCY_COLORS BLM
  // is a similar sandy brown — keeps the visual language consistent).
  GOVERNMENT: '#a16207',      // amber-700
  // Generic placeholder when a county uses a literal "INDIVIDUAL OWNER"
  // string instead of the real name (some assessors do this).
  INDIVIDUAL: '#64748b',      // slate-500
};

// Substrings that flag a government / public-land owner. Tested
// against the normalized (uppercase, single-spaced) owner string.
// Matches the conventions the federal_lands ingest uses.
const GOVERNMENT_PATTERNS = [
  'UNITED STATES',
  'USDA',
  'USFS',
  ' US FOREST',
  ' BLM',
  'BUREAU OF LAND',
  'BUREAU OF RECLAMATION',
  ' NPS',
  'NATIONAL PARK',
  'FOREST SERVICE',
  'FISH AND WILDLIFE',
  'FISH & WILDLIFE',
  'DEPARTMENT OF',
  'DEPT OF',
  'STATE OF ',
  'COMMONWEALTH OF',
  'CITY OF ',
  'COUNTY OF ',
  'TOWN OF ',
  'VILLAGE OF ',
  'BOROUGH OF ',
];

const INDIVIDUAL_PATTERNS = [
  'INDIVIDUAL OWNER',
  'CURRENT OWNER',
  'UNKNOWN OWNER',
  'OWNER UNKNOWN',
];

/**
 * Normalize an owner name for hashing / comparison.
 *
 * Same shape as the backend's `owner-portfolio` normalization
 * (`UPPER(REGEXP_REPLACE(TRIM(owner), '\\s+', ' ', 'g'))`).
 */
export function normalizeOwner(name) {
  if (name == null) return '';
  return String(name).trim().replace(/\s+/g, ' ').toUpperCase();
}

function fnv1a32(str) {
  // FNV-1a 32-bit hash. Tiny, deterministic, well-spread for short input.
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    // Math.imul keeps the multiply in 32-bit signed space (without it
    // JS would promote to a double and we'd lose the low bits).
    hash = Math.imul(hash, 0x01000193);
  }
  // Coerce to unsigned 32-bit for a stable positive integer.
  return hash >>> 0;
}

/**
 * Bucket an owner string into one of the reserved categories, or
 * return null when no bucket matches.
 */
export function ownerBucket(name) {
  const norm = normalizeOwner(name);
  if (!norm) return 'NULL_OWNER';
  for (const pat of INDIVIDUAL_PATTERNS) {
    if (norm.includes(pat)) return 'INDIVIDUAL';
  }
  for (const pat of GOVERNMENT_PATTERNS) {
    if (norm.includes(pat)) return 'GOVERNMENT';
  }
  return null;
}

/**
 * Return a deterministic HSL color for an owner name.
 *
 * Same input → same output, always. Reserved buckets short-circuit to
 * fixed colors; everything else hashes to a hue with saturation 65%
 * and lightness 50% (saturated mid-tone — readable on satellite tiles).
 */
export function ownerColor(name) {
  const bucket = ownerBucket(name);
  if (bucket) return RESERVED[bucket];
  const norm = normalizeOwner(name);
  const hash = fnv1a32(norm);
  const hue = hash % 360;
  return `hsl(${hue}, 65%, 50%)`;
}

/**
 * Slightly darker variant of `ownerColor`, suitable for polygon
 * outlines so the stroke stays visible against the fill.
 */
export function ownerStroke(name) {
  const bucket = ownerBucket(name);
  if (bucket) {
    // Slightly darker fixed pair for each reserved bucket.
    return {
      NULL_OWNER: '#6b7280',
      GOVERNMENT: '#78350f',
      INDIVIDUAL: '#475569',
    }[bucket];
  }
  const norm = normalizeOwner(name);
  const hash = fnv1a32(norm);
  const hue = hash % 360;
  return `hsl(${hue}, 65%, 35%)`;
}
